// A home patient's programs (staff): which ones, in what order, how often each should be
// done (its schedule), and how a session picks among them (the mode). Shows what is due and
// what is overdue.

import { useEffect, useState } from "react";
import { api, type Plan, type PlanItem, type Program, type ProgramMode, type Schedule } from "../api";
import { errorText } from "../hooks";
import { Help } from "./Help";

export const MODE_LABEL: Record<ProgramMode, string> = {
  staff_active: "Staff choose: the patient always runs the active program",
  schedule: "By schedule: each session runs whatever is due",
  patient_picks: "The patient picks a program each time",
  all: "All of them, one after another, every session",
};

const DEFAULT_SCHEDULE: Schedule = { kind: "patient_choice", n: 1, unit: "weeks", grace_days: 0 };

export function scheduleText(s: Schedule): string {
  if (s.kind === "patient_choice") return "patient's choice (unmonitored)";
  if (s.kind === "every_n_sessions") return s.n === 1 ? "every session" : `every ${s.n} sessions`;
  const unit = s.n === 1 ? s.unit.slice(0, -1) : s.unit;
  return `every ${s.n === 1 ? "" : `${s.n} `}${unit}${s.grace_days ? ` (${s.grace_days} days' grace)` : ""}`;
}

const day = (d: string | null) => (d ? new Date(d).toLocaleDateString() : "—");

export function statusText(it: PlanItem): { text: string; cls: string } {
  const st = it.status;
  if (st.optional) return { text: "when the patient likes", cls: "muted" };
  if (st.overdue_days) return { text: `overdue by ${st.overdue_days} day${st.overdue_days === 1 ? "" : "s"}`, cls: "bad" };
  if (st.due) return { text: "due now", cls: "warn" };
  if (st.next_due_at) return { text: `next due ${day(st.next_due_at)}`, cls: "" };
  if (st.sessions_until_due !== undefined) return { text: `due in ${st.sessions_until_due} session${st.sessions_until_due === 1 ? "" : "s"}`, cls: "" };
  return { text: "—", cls: "muted" };
}

interface Row { program_id: number; schedule: Schedule }

export function PatientPrograms({ patientId, programs, canEdit }: { patientId: number; programs: Program[]; canEdit: boolean }) {
  const [plan, setPlan] = useState<Plan | null>(null);
  const [mode, setMode] = useState<ProgramMode>("staff_active");
  const [rows, setRows] = useState<Row[]>([]);
  const [active, setActive] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);
  const [adding, setAdding] = useState<number | "">("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = (p: Plan) => {
    setPlan(p);
    setMode(p.mode);
    setRows(p.items.map((it) => ({ program_id: it.program_id, schedule: it.schedule })));
    setActive(p.items.find((it) => it.active)?.program_id ?? p.items[0]?.program_id ?? null);
    setDirty(false);
  };
  useEffect(() => { api.patientPrograms(patientId).then(load, (e) => setError(errorText(e))); }, [patientId]);

  const name = (id: number) => programs.find((p) => p.id === id)?.name ?? `program ${id}`;
  const change = (next: Row[]) => { setRows(next); setDirty(true); setSaved(false); };
  const setSchedule = (i: number, patch: Partial<Schedule>) =>
    change(rows.map((r, k) => (k === i ? { ...r, schedule: { ...r.schedule, ...patch } } : r)));
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j]!, next[i]!];
    change(next);
  };
  const save = async () => {
    setError(null);
    try {
      load(await api.setPatientPrograms(patientId, {
        mode, active_program_id: mode === "staff_active" ? active : null,
        items: rows.map((r) => ({ program_id: r.program_id, schedule: r.schedule })),
      }));
      setSaved(true);
    } catch (e) {
      setError(errorText(e));
    }
  };
  if (!plan) return <div className="card">{error ? <div className="error">{error}</div> : "Loading programs…"}</div>;
  const status = new Map(plan.items.map((it) => [it.program_id, it]));
  const unused = programs.filter((p) => p.active && !rows.some((r) => r.program_id === p.id));
  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Programs at home<Help k="plan.overview" /></h2>
      <label><span>How a session picks<Help k="plan.mode" /></span>
        <select aria-label="How a session picks" value={mode} disabled={!canEdit}
          onChange={(e) => { setMode(e.target.value as ProgramMode); setDirty(true); setSaved(false); }}>
          {(Object.keys(MODE_LABEL) as ProgramMode[]).map((m) => <option key={m} value={m}>{MODE_LABEL[m]}</option>)}
        </select>
      </label>
      <table className="req-table" style={{ marginTop: "0.6rem" }}>
        <thead><tr><th>#</th>{mode === "staff_active" && <th>Active</th>}<th>Program</th>
          <th>How often<Help k="plan.schedule" /></th><th>Last done</th><th>Status</th><th /></tr></thead>
        <tbody>
          {rows.map((r, i) => {
            const it = status.get(r.program_id);
            const st = it && !dirty ? statusText(it) : { text: dirty ? "save to update" : "—", cls: "muted" };
            const s = r.schedule;
            return (
              <tr key={r.program_id}>
                <td>{i + 1}</td>
                {mode === "staff_active" && (
                  <td><input type="radio" name="active-program" aria-label={`Active: ${name(r.program_id)}`} disabled={!canEdit}
                    checked={active === r.program_id} onChange={() => { setActive(r.program_id); setDirty(true); setSaved(false); }} /></td>
                )}
                <td>{name(r.program_id)}</td>
                <td>
                  <div className="row" style={{ gap: "0.3rem", alignItems: "center" }}>
                    <select aria-label={`${name(r.program_id)} schedule`} value={s.kind} disabled={!canEdit}
                      onChange={(e) => setSchedule(i, { kind: e.target.value as Schedule["kind"] })}>
                      <option value="patient_choice">patient's choice (unmonitored)</option>
                      <option value="every_n_sessions">every N sessions</option>
                      <option value="interval">every N days / weeks / months</option>
                    </select>
                    {s.kind !== "patient_choice" && (
                      <input type="number" min={1} max={365} style={{ width: "4.5rem" }} aria-label={`${name(r.program_id)} every N`}
                        value={s.n} disabled={!canEdit}
                        onChange={(e) => setSchedule(i, { n: Math.max(1, Math.min(365, Math.round(Number(e.target.value) || 1))) })} />
                    )}
                    {s.kind === "interval" && (
                      <>
                        <select aria-label={`${name(r.program_id)} unit`} value={s.unit} disabled={!canEdit}
                          onChange={(e) => setSchedule(i, { unit: e.target.value as Schedule["unit"] })}>
                          <option value="days">days</option><option value="weeks">weeks</option><option value="months">months</option>
                        </select>
                        <label style={{ flexDirection: "row", gap: "0.2rem" }} className="small">grace
                          <input type="number" min={0} max={60} style={{ width: "3.8rem" }} aria-label={`${name(r.program_id)} grace days`}
                            value={s.grace_days} disabled={!canEdit}
                            onChange={(e) => setSchedule(i, { grace_days: Math.max(0, Math.min(60, Math.round(Number(e.target.value) || 0))) })} />days</label>
                      </>
                    )}
                  </div>
                </td>
                <td className="small">{it ? day(it.status.last_done_at) : "—"}</td>
                <td className={`small ${st.cls}`}>{st.text}</td>
                <td className="row-actions" style={{ whiteSpace: "nowrap" }}>
                  {canEdit && (
                    <>
                      <button className="link" aria-label={`Move ${name(r.program_id)} up`} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                      <button className="link" aria-label={`Move ${name(r.program_id)} down`} disabled={i === rows.length - 1} onClick={() => move(i, 1)}>↓</button>
                      <button className="link" aria-label={`Remove ${name(r.program_id)}`} disabled={rows.length === 1}
                        onClick={() => change(rows.filter((_, k) => k !== i))}>Remove</button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {canEdit && (
        <div className="row" style={{ alignItems: "flex-end", marginTop: "0.5rem" }}>
          <label>Add a program
            <select aria-label="Add a program" value={adding} onChange={(e) => setAdding(e.target.value ? Number(e.target.value) : "")}>
              <option value="">choose…</option>
              {unused.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <button className="secondary" disabled={adding === ""} onClick={() => {
            if (adding === "") return;
            change([...rows, { program_id: adding, schedule: { ...DEFAULT_SCHEDULE } }]);
            setAdding("");
          }}>Add</button>
          <button disabled={!dirty || rows.length === 0} onClick={() => void save()}>Save programs</button>
          {saved && <span className="small good">Saved.</span>}
        </div>
      )}
      <p className="small muted">{mode === "schedule"
        ? "Each session runs the programs that are due, in this order; programs on the patient's own schedule are offered as extras."
        : mode === "all" ? "Each session runs every program, in this order (one screen check and one brightness reading for all)."
          : mode === "patient_picks" ? "The patient chooses one program each time; the schedules show staff what is due or overdue."
            : "The patient runs the active program; the schedules show staff what is due or overdue."}</p>
      {error && <div className="error">{error}</div>}
    </div>
  );
}
